import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, LogOut } from "lucide-react";
import CookieBanner from "./components/CookieBanner";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import AIAssistant from "./components/AIAssistant";

import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaEnvelope,
} from "react-icons/fa";

export default function Layout() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdown, setDropdown] = useState(null);
  const [membre, setMembre] = useState(null);
  const [token, setToken] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const storedMembre = localStorage.getItem("membre");
    const storedToken = localStorage.getItem("membreToken");

    if (storedMembre) {
      try {
        setMembre(JSON.parse(storedMembre));
      } catch {
        setMembre(null);
      }
    }

    if (storedToken) {
      setToken(storedToken);
    }
  }, [location.pathname]);

  /* =========================
      🔐 ROLE ADMIN
   ========================== */
  let isAdmin = false;

  if (token) {
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      isAdmin = decoded.role === "admin";
    } catch {}
  }

  const isMemberLogged = !!membre && !!token;

  const logout = () => {
    localStorage.removeItem("membre");
    localStorage.removeItem("membreToken");
    localStorage.removeItem("membreExpiresAt");
    setMembre(null);
    setToken(null);
    navigate("/");
  };

  const nav = [
    { name: t("nav.home"), href: "/" },
    { name: t("nav.about"), href: "/about" },
    {
      name: t("nav.programs"),
      href: "/activities",
      children: [
        { name: t("nav.activitiesEvents"), href: "/activities" },
        { name: t("nav.gallery"), href: "/gallery" },
      ],
    },
    { name: t("nav.members"), href: "/members" },
    { name: t("nav.partners"), href: "/partners" },
    { name: t("nav.news"), href: "/blog" },
    { name: t("nav.contact"), href: "/contact" },
  ];

  const active = (item) => {
    const current = location.pathname;
    if (item.href === "/") return current === "/";
    if (current === item.href) return true;
    if (current.startsWith(item.href) && item.href !== "/") return true;

    if (item.children) {
      return item.children.some((child) => current.startsWith(child.href));
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-paragraph">
      <header className="sticky top-0 z-[100] bg-white/95 backdrop-blur border-b border-black/10 shadow-sm">
        <nav className="max-w-[110rem] mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="images/aleoa2.png"
              alt="Logo"
              className="w-12 h-12 rounded-lg object-cover transition group-hover:scale-105"
            />
            <div className="leading-tight hidden sm:block">
              <h1 className="text-l font-heading font-bold tracking-wide text-black">
                {t("brand.name")}
              </h1>
              <p className="text-[#007BFF] font-heading text-xs font-bold -mt-1 uppercase tracking-wider">
                {t("brand.excellence")}
              </p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center flex-grow justify-center">
            <div className="flex items-center gap-6">
              {nav.map((item, i) =>
                item.children ? (
                  <div
                    key={i}
                    className="relative"
                    onMouseEnter={() => setDropdown(i)}
                    onMouseLeave={() => setDropdown(null)}
                  >
                    <button
                      className={`flex items-center gap-1 font-heading uppercase text-xs transition-all ${
                        active(item)
                          ? "text-primary"
                          : "text-black hover:text-primary"
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="h-3 w-3" />
                    </button>

                    <AnimatePresence>
                      {dropdown === i && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-3 w-48 bg-white border border-black/10 shadow-lg rounded-lg py-2 z-[101]"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-2 text-xs uppercase hover:text-primary"
                            >
                              {child.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={i}
                    to={item.href}
                    className={`font-heading uppercase text-xs transition-all ${
                      active(item)
                        ? "text-primary"
                        : "text-black hover:text-primary"
                    }`}
                  >
                    {item.name}
                  </Link>
                ),
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            {!isMemberLogged ? (
              <Link
                to="/membre/login"
                className="px-3 py-1.5 rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition font-semibold text-xs"
              >
                {t("auth.login")}
              </Link>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    (window.location.href = isAdmin
                      ? "/admin"
                      : "/membre/dashboard")
                  }
                  className="relative flex items-center gap-2 px-2 py-1 rounded-md hover:bg-black/5 transition max-w-[150px]"
                >
                  {!isAdmin ? (
                    <img
                      src={membre?.avatar || "/images/default-avatar.png"}
                      className="w-8 h-8 rounded-full object-cover"
                      alt="avatar"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white font-bold">
                      {membre?.prenom?.charAt(0)}
                    </div>
                  )}

                  {isAdmin && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full shadow">
                      👑
                    </span>
                  )}
                </button>

                <button
                  onClick={logout}
                  className="p-2 rounded-full text-red-600 hover:bg-red-100 transition"
                >
                  <LogOut size={18} />
                </button>
              </div>
            )}

            <Link
              to="/members"
              className="px-3 py-1.5 rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition font-semibold text-xs"
            >
              {t("auth.join")}
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher />
            {isMemberLogged && (
              <button
                onClick={() =>
                  (window.location.href = isAdmin
                    ? "/admin"
                    : "/membre/dashboard")
                }
                className="relative flex items-center gap-2"
              >
                {!isAdmin ? (
                  <img
                    src={membre?.avatar || "/images/default-avatar.png"}
                    className="w-8 h-8 rounded-full object-cover"
                    alt="avatar"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white font-bold text-xs">
                    {membre?.prenom?.charAt(0)}
                  </div>
                )}

                {isAdmin && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[8px] px-1 rounded-full shadow">
                    👑
                  </span>
                )}
              </button>
            )}

            <button
              className="text-black"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                className="fixed inset-0 bg-black/40 z-[98]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMenuOpen(false)}
              />

              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 70, damping: 15 }}
                className="fixed top-0 right-0 w-full bg-white z-[99] p-6 shadow-xl rounded-b-3xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-heading text-lg font-bold">
                    {t("nav.menu")}
                  </span>
                  <button onClick={() => setMenuOpen(false)}>
                    <X size={28} className="text-black" />
                  </button>
                </div>

                <div className="flex flex-col gap-3">
                  {nav.map((item, i) =>
                    item.children ? (
                      <div key={i} className="border-b pb-2">
                        <button
                          onClick={() => setDropdown(dropdown === i ? null : i)}
                          className="flex items-center justify-between w-full text-black font-medium py-2"
                        >
                          {item.name}
                          <ChevronDown
                            className={`transition-transform ${
                              dropdown === i ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence>
                          {dropdown === i && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-3 flex flex-col gap-2 mt-1"
                            >
                              {item.children.map((sub) => (
                                <Link
                                  key={sub.href}
                                  to={sub.href}
                                  onClick={() => setMenuOpen(false)}
                                  className="py-2 text-sm text-black/80 hover:text-primary"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={i}
                        to={item.href}
                        onClick={() => setMenuOpen(false)}
                        className="py-2 text-black font-medium hover:text-primary border-b"
                      >
                        {item.name}
                      </Link>
                    ),
                  )}
                </div>

                {!isMemberLogged ? (
                  <>
                    <Link
                      to="/membre/login"
                      onClick={() => setMenuOpen(false)}
                      className="mt-6 block text-center bg-primary text-white py-2 rounded-lg font-semibold"
                    >
                      {t("auth.login")}
                    </Link>

                    <Link
                      to="/members"
                      onClick={() => setMenuOpen(false)}
                      className="mt-3 block text-center border border-primary text-primary py-2 rounded-lg font-semibold hover:bg-primary hover:text-white transition"
                    >
                      {t("auth.join")}
                    </Link>
                  </>
                ) : (
                  <div className="mt-6 flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        window.location.href = isAdmin
                          ? "/admin"
                          : "/membre/dashboard";
                      }}
                      className="flex items-center gap-3 p-3 rounded-lg bg-black/5"
                    >
                      {!isAdmin ? (
                        <img
                          src={membre?.avatar || "/images/default-avatar.png"}
                          className="w-8 h-8 rounded-full object-cover"
                          alt="avatar"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-bold">
                          {membre?.prenom?.charAt(0)}
                        </div>
                      )}

                      <span className="font-semibold truncate">
                        {membre?.prenom?.toUpperCase()}
                      </span>

                      {isAdmin && <span className="ml-1">👑</span>}
                    </button>

                    <button
                      onClick={() => {
                        localStorage.removeItem("membre");
                        localStorage.removeItem("membreToken");
                        setMenuOpen(false);
                        window.location.href = "/";
                      }}
                      className="w-full py-2 rounded-lg border border-red-500 text-red-600 font-semibold"
                    >
                      {t("auth.logout")}
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-foreground text-background">
        <div className="container max-w-[100rem] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center md:text-left">
            <div className="lg:col-span-2 flex flex-col items-center md:items-start">
              <Link to="/" className="flex items-center space-x-3 mb-6">
                <img
                  src="images/aleoa.jpg"
                  alt="Logo"
                  className="w-14 h-14 object-contain rounded-full"
                />
                <div className="text-left">
                  <span className="font-heading font-bold text-xl">
                    {t("footer.associationName")}
                  </span>

                  <div className="font-heading font-bold text-sm text-electric-blue">
                    {t("footer.associationExcellence")}
                  </div>
                </div>
              </Link>

              <p className="font-paragraph text-background/80 mb-6 max-w-md leading-relaxed">
                {t("footer.description")}
              </p>

              <div className="flex justify-center md:justify-start gap-4 mt-4">
                <a
                  href="https://www.facebook.com/profile.php?id=100069377171465&locale=fr_FR"
                  target="_blank"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">
                    <FaFacebookF />
                  </div>
                </a>
                <a
                  href="https://www.instagram.com/association_aleoa/"
                  target="_blank"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-pink-500 text-white rounded-full hover:bg-pink-600 transition">
                    <FaInstagram />
                  </div>
                </a>
                <a
                  href="https://wa.me/212617679966"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#1EBE5C] transition">
                    <FaWhatsapp />
                  </div>
                </a>
                <a
                  href="mailto:aleoaassociation@gmail.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div
                    className="
      w-10 h-10 flex items-center justify-center rounded-full text-white 
      bg-gradient-to-br from-[#DB4437] via-[#F4B400] to-[#4285F4]
      hover:from-[#4285F4] hover:via-[#0F9D58] hover:to-[#DB4437]
      transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105
    "
                  >
                    <FaEnvelope className="text-lg" />
                  </div>
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-heading font-bold text-lg mb-4">
                {t("footer.quickLinks")}
              </h3>
              <ul className="space-y-3 font-paragraph">
                <li>
                  <Link
                    to="/about"
                    className="text-background/80 hover:text-electric-blue transition"
                  >
                    {t("nav.about")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/activities"
                    className="text-background/80 hover:text-electric-blue transition"
                  >
                    {t("nav.programs")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/members"
                    className="text-background/80 hover:text-electric-blue transition"
                  >
                    {t("nav.members")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className="text-background/80 hover:text-electric-blue transition"
                  >
                    {t("nav.gallery")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-heading font-bold text-lg mb-4">
                {t("footer.contact")}
              </h3>
              <ul className="space-y-3 font-paragraph text-background/80">
                <li>Oulmes, Maroc</li>
                <li>
                  <a
                    className="hover:text-electric-blue transition"
                    target="_blank"
                    href="https://www.google.com/maps/place/%D8%AF%D8%A7%D8%B1+%D8%A7%D9%84%D8%B4%D8%A8%D8%A7%D8%A8+%D9%88%D8%A7%D9%84%D9%85%D8%A7%D8%B3%E2%80%AD/@33.4218807,-6.0044591,137m/data=!3m1!1e3!4m6!3m5!1s0xda1176cced36e91:0xe50da80d3fe7d2ee!8m2!3d33.421773!4d-6.0047723!16s%2Fg%2F11tms8gpwj?entry=ttu&g_ep=EgoyMDI1MTEzMC4wIKXMDSoASAFQAw%3D%3D"
                  >
                    Dar Echabab , دار الشباب{" "}
                  </a>
                </li>
                <li className="pt-2">
                  <a
                    href="tel:+212636831567"
                    className="hover:text-electric-blue transition"
                    target="_blank"
                  >
                    +212 6 17 67 99 66
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:aleoaassociation@gmail.com"
                    className="hover:text-electric-blue transition"
                    target="_blank"
                  >
                    aleoaassociation@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-background/20 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between text-center md:text-left">
            <p className="font-paragraph text-background/60 text-sm">
              {t("footer.copyright")}
            </p>

            <div className="flex flex-wrap justify-center gap-6 mt-4 md:mt-0">
              <Link
                to="/PrivacyPolicy"
                className="font-paragraph text-background/60 hover:text-electric-blue text-sm transition"
              >
                {t("footer.privacy")}
              </Link>
              <Link
                to="/Terms"
                className="font-paragraph text-background/60 hover:text-electric-blue text-sm transition"
              >
                {t("footer.terms")}
              </Link>
              <Link
                to="/Cookies"
                className="font-paragraph text-background/60 hover:text-electric-blue text-sm transition"
              >
                {t("footer.cookies")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
      <CookieBanner />
      <AIAssistant />
    </div>
  );
}
